//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import SensorModel from './sensor.model';

/**
 * @service SensorService
 * @description Service for managing Sensor configurations within the aquaculture system.
 * Sensors are physical devices deployed in ponds or tanks to monitor environmental parameters
 * such as Dissolved Oxygen (DO), pH, Temperature, and Salinity.
 *
 * This service provides the interface to retrieve the master list of sensors configured in the system.
 * It interacts with the `/aqu/sensor/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Sensor Registry**: Fetching the list of all available sensors.
 * 2. **Configuration**: Supporting the management of sensor metadata (vendor, type, associated parameters).
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Retrieve all defined Sensors.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure all sensors are loaded.
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<SensorModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/sensor/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class SensorService extends Service<SensorModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/sensor/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/sensor/');
  }

  /**
   * @method getAllTSensors
   * @async
   * @description Fetch all Sensor records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all sensors are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<SensorModel>>} Promise resolving to an array of SensorModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * sensorService.getAllTSensors().then(sensors => {
   *   console.log('Configured Sensors:', sensors);
   * });
   */
  public getAllTSensors() {
    return new Promise<Array<SensorModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<SensorModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
